import dayjs from 'dayjs';
import { NumericFormat } from 'react-number-format';

import { GiftBagIcon, LocationIcon, ShieldCheckIcon } from '../../../../../assets/svg';
import { usePayLinkCart } from '../../../context/PayLinkCartContext';
import { usePayLinkUser } from '../../../context/PaylinkUserContext';
import { EarnableRewards } from './reward/EarnableRewards';

interface IProps {
  currentView: number;
  cartTotal: number;
  rewardDiscount: number;
  pointDiscount: number;
  shippingFee: number;
  amountToPay: number;
}

export const CartSummary = ({
  currentView,
  cartTotal,
  rewardDiscount,
  pointDiscount,
  shippingFee,
  amountToPay,
}: IProps) => {
  const { currency } = usePayLinkCart();
  const { shippingRate, freeShippingReward } = usePayLinkUser();

  const originalShippingFee = Math.max(0, Number(shippingRate?.charge ?? 0));
  const effectiveShippingFee = Math.max(0, Number(shippingFee ?? 0));
  const shippingSavings = Math.max(0, originalShippingFee - effectiveShippingFee);
  const hasShippingRewardApplied = !!freeShippingReward && shippingSavings > 0;

  const normalizedRewardDiscount = Math.max(0, Number(rewardDiscount ?? 0));
  const rewardHasKobo = normalizedRewardDiscount % 1 !== 0;
  const roundedRewardDiscount = rewardHasKobo
    ? Math.ceil(normalizedRewardDiscount * 100) / 100
    : normalizedRewardDiscount;
  const assistedShippingEta = shippingRate?.deliveryEta || "ETA unavailable";
  const assistedShippingArrival = shippingRate?.deliveryEtaTime
    ? dayjs(shippingRate.deliveryEtaTime).isValid()
      ? dayjs(shippingRate.deliveryEtaTime).format("ddd, DD MMM")
      : shippingRate.deliveryEtaTime
    : "Arrival date unavailable";

  return (
    <div className="flex flex-col !gap-2 w-full">
      <ul className="flex flex-col !gap-2 !p-4 border border-py_black-100 rounded-2xl lg:!p-0 lg:!border-none">
        <li className="text-base text-py_black-950 font-medium leading-[140%] tracking-[-0.02em] !py-2 flex items-center justify-between">
          <span>Cart total</span>

          <NumericFormat
            value={cartTotal}
            displayType={"text"}
            thousandSeparator={true}
            prefix={currency}
            className="text-base text-[#121212] font-semibold leading-[140%] tracking-[-0.02em]"
          />
        </li>

        {currentView > 2 && roundedRewardDiscount > 0 && (
          <li className="text-base text-py_black-950 font-medium leading-[140%] tracking-[-0.02em] !py-2 flex items-center justify-between">
            <span>Reward discount</span>

            <NumericFormat
              value={roundedRewardDiscount}
              displayType={"text"}
              thousandSeparator={true}
              decimalScale={rewardHasKobo ? 2 : 0}
              fixedDecimalScale={rewardHasKobo}
              prefix={`-${currency}`}
              className="text-base text-[#121212] font-semibold leading-[140%] tracking-[-0.02em]"
            />
          </li>
        )}

        {currentView > 2 && pointDiscount > 0 && (
          <li className="text-base text-py_black-950 font-medium leading-[140%] tracking-[-0.02em] !py-2 flex items-center justify-between">
            <span>Points redeemed</span>

            <NumericFormat
              value={Math.max(0, pointDiscount)}
              displayType={"text"}
              thousandSeparator={true}
              prefix={`-${currency}`}
              className="text-base text-[#121212] font-semibold leading-[140%] tracking-[-0.02em]"
            />
          </li>
        )}

        {currentView > 0 && shippingRate && (
          <li className="text-base text-py_black-950 font-medium leading-[140%] tracking-[-0.02em] !py-2">
            <div className="flex justify-between">
              <span className="font-normal">Shipping fee</span>

              {hasShippingRewardApplied ? (
                <div className="flex items-center gap-2">
                  <NumericFormat
                    value={originalShippingFee}
                    displayType={"text"}
                    thousandSeparator={true}
                    prefix={currency}
                    className="text-sm text-py_black-500 font-medium line-through"
                  />
                  <NumericFormat
                    value={effectiveShippingFee.toFixed(1)}
                    displayType={"text"}
                    thousandSeparator={true}
                    prefix={currency}
                    className="text-base text-[#121212] font-semibold leading-[140%] tracking-[-0.02em]"
                  />
                </div>
              ) : (
                <NumericFormat
                  value={effectiveShippingFee}
                  displayType={"text"}
                  thousandSeparator={true}
                  prefix={currency}
                  className="text-base text-[#121212] font-semibold leading-[140%] tracking-[-0.02em]"
                />
              )}
            </div>

            {hasShippingRewardApplied && (
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center !gap-1 rounded-[100px] bg-py_green_1-300 !py-[0.25rem] !px-2 w-fit">
                  <GiftBagIcon />
                  <span className="text-[0.75rem] text-py_black-950 font-medium">
                    {effectiveShippingFee === 0 ? "Free shipping" : "Shipping reward applied"}
                  </span>
                </span>
              </div>
            )}

            {shippingRate.isAssistedShipping && (
              <div className="flex flex-col gap-2 mt-1">
                <div className="flex justify-between">
                  <span className="w-fit h-8 inline-flex items-center gap-1 p-2 text-[0.615rem] lg:text-xs font-medium rounded-[999px] border border-py_green_1-300 bg-py_green_1-100 text-py_green_1-700">
                    <ShieldCheckIcon />
                    Paycurra Assisted Shipping
                  </span>
                  <small className="text-py_green_1-700 font-medium text-[0.615rem] lg:text-xs">
                    Calculated by Paycurra
                  </small>
                </div>

                <span className="w-fit h-8 inline-flex items-center gap-1 p-2 text-[0.615rem] lg:text-xs font-medium rounded-[999px] border border-py_black-300 bg-py_black-100 text-py_black-700">
                  <LocationIcon />
                  ETA: {assistedShippingEta} • Arrives by {assistedShippingArrival}
                </span>
              </div>
            )}
          </li>
        )}
      </ul>

      {currentView >= 2 && (
        <div className="!mt-2">
          <EarnableRewards />
        </div>
      )}

      {currentView > 2 && (
        <div className="!mt-2 text-base text-py_black-950 font-medium leading-[140%] tracking-[-0.02em] !py-5 border-y border-py_black-100 flex items-center justify-between">
          <span className="text-lg">Amount to pay</span>

          <NumericFormat
            value={amountToPay}
            displayType={"text"}
            thousandSeparator={true}
            prefix={currency}
            className="text-base text-[#121212] font-semibold leading-[140%] tracking-[-0.02em]"
          />
        </div>
      )}
    </div>
  );
};
